import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import { Form } from 'react-bootstrap'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { useUpdateAccountMutation } from '../../../../store/accountUserSettings/accountUserSettings'
import { useGetAllUsersQuery } from '../../../../store/user/user'
import { Account, MeetingProcessedDoneEmailConfig } from '../../../../models/Account'
import { MultiSelectDropdown, MultiSelectOption } from '../../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { Button } from '../../../../components/Button/Button'
import { useZocksAuth } from '../../../../components/ScopedBlock/hooks'
import { EnterpriseManagedBanner } from '../EnterpriseManagedBanner'
import { WhoGetsNotifiedHeader } from './WhoGetsNotifiedHeader'

interface MeetingProcessedDoneFormValues {
  includeInfo: 'linkOnly' | 'linkAndMeetingInfo' | 'linkAndMeetingInfoAndSummary'
  includeTitleInSubject: boolean
  teamMemberIds: string[]
}

const getDefaultFormValues = (config: MeetingProcessedDoneEmailConfig): MeetingProcessedDoneFormValues => ({
  includeInfo: config.includeInfo,
  includeTitleInSubject: config.includeTitleInSubject,
  teamMemberIds: config.notifications.teamMemberIds,
})

export interface MeetingProcessedDoneNotificationProps {
  account: Account
}

export const MeetingProcessedDoneNotification = ({ account }: MeetingProcessedDoneNotificationProps) => {
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const [updateAccount, { isLoading: accountUpdateLoading }] = useUpdateAccountMutation()

  const { data: usersData, isFetching: isUsersLoading } = useGetAllUsersQuery({ accountId, identityTypes: ['idp'] })
  const [isOpened, setIsOpened] = useState<boolean>(false)
  const { hasScope } = useZocksAuth()
  const hasAccountUpdateScope = hasScope('**', 'account:update', ['account:admin'])

  const isMeetingProcessedDoneEmailEnterpriseManaged = useMemo(() => account?.enterpriseConfig.meetingProcessedDoneEmail != null, [account])

  const teamMemberOptions: MultiSelectOption[] = useMemo(() => {
    if (!usersData?.data) {
      return []
    }
    return usersData.data
      .filter((u) => u.id !== currentUserId)
      .sort((u1, u2) => getUserDisplayName(u1).localeCompare(getUserDisplayName(u2)))
      .map((u) => ({ id: u.id, label: getUserDisplayName(u) }))
  }, [usersData, currentUserId])

  const { control, handleSubmit, reset, formState } = useForm<MeetingProcessedDoneFormValues>({
    defaultValues: getDefaultFormValues(account.effectiveConfig.meetingProcessedDoneEmail),
  })

  const includeInfo = useWatch({ control, name: 'includeInfo' })

  const onSubmit = async (data: MeetingProcessedDoneFormValues) => {
    const meetingProcessedDoneEmail: MeetingProcessedDoneEmailConfig = {
      includeInfo: data.includeInfo,
      includeTitleInSubject: data.includeTitleInSubject ?? false,
      notifications: {
        teamMemberIds: data.teamMemberIds.length > 0 ? data.teamMemberIds : [],
      },
    }

    const result = await updateAccount({
      accountId,
      payload: {
        config: {
          ...account.config,
          meetingProcessedDoneEmail,
        },
      },
    })
    if ('data' in result && result.data) {
      reset(getDefaultFormValues(result.data.data.effectiveConfig.meetingProcessedDoneEmail))
    }
  }

  const handleCancel = () => {
    reset(getDefaultFormValues(account.effectiveConfig.meetingProcessedDoneEmail))
  }

  return (
    <div className='d-flex flex-column gap-4 border-bottom zocks-pb-2 zocks-border-color-gray-400 border-1'>
      {isMeetingProcessedDoneEmailEnterpriseManaged ? (
        <EnterpriseManagedBanner message='Meeting Processing Done Email setting is managed at the enterprise level and cannot be modified here.' />
      ) : null}
      <div className='d-flex zocks-gap-0.75 align-items-center justify-content-between'>
        <div className='d-flex flex-column'>
          <h6>Meeting Processing Done</h6>
          <div className='paragraph-small zocks-text-gray-700'>Sent when meeting processing finished and past meeting is ready.</div>
        </div>
        <div className='d-flex flex-row gap-2 align-items-center'>
          <Button onClick={() => setIsOpened((prev) => !prev)}>
            <i className={isOpened ? 'bi bi-chevron-up' : 'bi bi-chevron-down'}></i>
          </Button>
        </div>
      </div>
      {isOpened ? (
        <>
          <div className='d-flex flex-column zocks-gap-0.75'>
            <div className='paragraph-medium medium'>Email content</div>
            <Controller
              name='includeInfo'
              control={control}
              render={({ field }) => (
                <div className='d-flex flex-column gap-2'>
                  <Form.Check
                    type='radio'
                    id='include-link-only-meeting-processed'
                    label='Link only'
                    value='linkOnly'
                    checked={field.value === 'linkOnly'}
                    onChange={() => field.onChange('linkOnly')}
                    disabled={accountUpdateLoading || !hasAccountUpdateScope || isMeetingProcessedDoneEmailEnterpriseManaged}
                  />
                  <Form.Check
                    type='radio'
                    id='include-link-and-meeting-info-meeting-processed'
                    label='Link and meeting details (title, date, participants)'
                    value='linkAndMeetingInfo'
                    checked={field.value === 'linkAndMeetingInfo'}
                    onChange={() => field.onChange('linkAndMeetingInfo')}
                    disabled={accountUpdateLoading || !hasAccountUpdateScope || isMeetingProcessedDoneEmailEnterpriseManaged}
                  />
                  <Form.Check
                    type='radio'
                    id='include-link-and-summary-meeting-processed'
                    label='Link, meeting details (title, date, participants) and summary'
                    value='linkAndMeetingInfoAndSummary'
                    checked={field.value === 'linkAndMeetingInfoAndSummary'}
                    onChange={() => field.onChange('linkAndMeetingInfoAndSummary')}
                    disabled={accountUpdateLoading || !hasAccountUpdateScope || isMeetingProcessedDoneEmailEnterpriseManaged}
                  />
                </div>
              )}
            />
            {includeInfo !== 'linkOnly' ? (
              <div className='d-flex flex-column zocks-gap-0.75'>
                <div className='paragraph-medium medium'>Email subject</div>
                <Controller
                  name='includeTitleInSubject'
                  control={control}
                  render={({ field }) => (
                    <Form.Check
                      type='checkbox'
                      id='include-title-in-subject-processed'
                      label='Include meeting title in subject'
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      disabled={accountUpdateLoading || !hasAccountUpdateScope || isMeetingProcessedDoneEmailEnterpriseManaged}
                    />
                  )}
                />
              </div>
            ) : null}
          </div>
          <div className='d-flex flex-column zocks-gap-0.75'>
            <WhoGetsNotifiedHeader />
            <div> Select team members who will receive the email notification in addition to the meeting owner </div>
            <div className='d-flex flex-column gap-2'>
              <div>
                <Controller
                  name='teamMemberIds'
                  control={control}
                  render={({ field }) => (
                    <MultiSelectDropdown
                      name={field.name}
                      options={teamMemberOptions}
                      initialSelectedOptions={field.value}
                      onSelectedChanged={field.onChange}
                      placeholder='Select team members…'
                      disabled={accountUpdateLoading || isUsersLoading || !hasAccountUpdateScope || isMeetingProcessedDoneEmailEnterpriseManaged}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className='d-flex zocks-gap-0.625'>
            <Button
              variant='primary'
              onClick={handleSubmit(onSubmit)}
              loading={accountUpdateLoading}
              disabled={accountUpdateLoading || !formState.isDirty || !hasAccountUpdateScope || isMeetingProcessedDoneEmailEnterpriseManaged}
            >
              Save changes
            </Button>
            <Button
              variant='borderless'
              className={classNames({ 'zocks-text-primary': !accountUpdateLoading && formState.isDirty })}
              onClick={handleCancel}
              disabled={accountUpdateLoading || !formState.isDirty || !hasAccountUpdateScope || isMeetingProcessedDoneEmailEnterpriseManaged}
            >
              Cancel
            </Button>
          </div>
        </>
      ) : null}
    </div>
  )
}
